import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AseguradoraRepository } from 'src/app/domain/service/aseguradora/aseguradora-repository';
import { Aseguradora, AseguradoraFiltro, AseguradoraPageResponse } from '@model/aseguradora/aseguradora';

/**
 * Adaptador HTTP: HttpAseguradoraRepository
 *
 * Implementa el puerto AseguradoraRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (AseguradoraController).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto AseguradoraRepository del dominio
 *
 * Backend: 580-girop_servicios-ms (puerto 8080)
 *
 * @author Sura - Migración GWT a Spring Boot
 */
@Injectable({ providedIn: 'root' })
export class HttpAseguradoraRepository implements AseguradoraRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/aseguradoras`;
  private static readonly PAGE_SIZE_MIN = 1;
  private static readonly PAGE_SIZE_MAX = 100;
  private static readonly DEFAULT_SIZE = 20;

  async buscarAseguradoras(
    filtro: AseguradoraFiltro,
    cursor: string | null,
    size = HttpAseguradoraRepository.DEFAULT_SIZE,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST' = 'NEXT'
  ): Promise<AseguradoraPageResponse> {
    this.validarPaginacion(size);

    let params = new HttpParams();

    if (filtro.tipoIdentificacion?.trim()) {
      params = params.set('tipoIdentificacion', filtro.tipoIdentificacion.trim());
    }
    if (filtro.numeroIdentificacion?.trim()) {
      params = params.set('numeroIdentificacion', filtro.numeroIdentificacion.trim());
    }
    if (filtro.nombreComercial?.trim()) {
      params = params.set('nombreComercial', filtro.nombreComercial.trim());
    }
    for (const campo of ['nombre1', 'nombre2', 'apellido1', 'apellido2'] as const) {
      const valor = filtro[campo]?.trim();
      if (valor) {
        params = params.set(campo, valor);
      }
    }
    if (filtro.activo !== undefined && filtro.activo.trim() !== '') {
      params = params.set('activo', filtro.activo.trim().toUpperCase());
    }
    params = params.set('size', size.toString());
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    params = params.set('direction', direction);

    return firstValueFrom(
      this.http.get<AseguradoraPageResponse>(`${this.baseUrl}/search`, { params })
    );
  }

  async obtenerAseguradoraPorNit(nit: string): Promise<Aseguradora> {
    return firstValueFrom(
      this.http.get<Aseguradora>(`${this.baseUrl}/por-nit/${nit}`)
    );
  }

  /**
   * Legacy: ApplicationCommonData.searchDefaultClient()
   *
   * Consume contrato de dominio backend que resuelve internamente
   * CLIENTE_DEFAULT_CIA y retorna la aseguradora por defecto.
   *
   * Retorna null si no está configurada o si falla la consulta para no bloquear
   * pantallas que la consumen como precarga opcional.
   */
  async obtenerAseguradoraDefault(): Promise<Aseguradora | null> {
    try {
      return await firstValueFrom(
        this.http.get<Aseguradora | null>(`${this.baseUrl}/default`)
      );
    } catch {
      return null;
    }
  }

  private validarPaginacion(size: number): void {
    if (!Number.isInteger(size) || size < HttpAseguradoraRepository.PAGE_SIZE_MIN || size > HttpAseguradoraRepository.PAGE_SIZE_MAX) {
      throw new Error(
        `size debe estar entre ${HttpAseguradoraRepository.PAGE_SIZE_MIN} y ${HttpAseguradoraRepository.PAGE_SIZE_MAX}.`
      );
    }
  }
}
